import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { CheckeredBgCard } from 'components/checkered-bg-card/CheckeredBgCard'
import { DottedBackgroundCard } from 'components/dotted-background-card/DottedBackgroundCard'
import GradientOutlineGridBackgroundCard from 'components/gradient-outline-grid-background-card/GradientOutlineGridBackgroundCard'
import { OutlineGlassBackgroundCard } from 'components/outline-glass-background-card/OutlineGlassBackgroundCard'
import { Text, TextName } from 'sdk'
import { Button } from 'sdk/button/Button'
import { Divider } from 'sdk/divider/Divider'

import type { PlatformGettingStartedCmsContent } from 'core/services/cms/interfaces'

import classes from './style.module.scss'

interface GettingStartedSectionProps {
  content: PlatformGettingStartedCmsContent
}

export const GettingStartedSection: React.FC<GettingStartedSectionProps> = ({ content }) => {
  const navigate = useNavigate()
  const isExternalResourcesButtonUrl = /^(https?:\/\/|mailto:|tel:)/.test(content.resourcesButtonUrl)

  const handleResourcesClick = (): void => {
    if (isExternalResourcesButtonUrl) {
      window.location.assign(content.resourcesButtonUrl)
      return
    }

    navigate(content.resourcesButtonUrl)
  }

  return (
    <section className={classes.content}>
      <Text type={TextName.H2} className={classes.title}>{content.title}</Text>

      <Divider pillText={content.supportedHardwareTitle} className={classes.supportedHardwareDivider} />

      <div className={classes.cardsContainer}>
        {content.supportedHardwareCards.map((card, index) => (
          <OutlineGlassBackgroundCard key={index} className={classes.card}>
            <img src={card.iconUrl} alt='' width={50} height={50} />
            <Text type={TextName.H3} className={classes.cardTitle}>{card.title}</Text>
            <ul className={classes.list}>
              {card.items.map((item, itemIndex) => (
                <li key={itemIndex}>
                  <Text type={TextName.Text}>{item}</Text>
                </li>
              ))}
            </ul>
          </OutlineGlassBackgroundCard>
        ))}
      </div>

      <Divider pillText={content.integrationOptionsTitle} className={classes.integrationOptionsDivider} />

      <div className={classes.integrationsGrid}>
        <CheckeredBgCard className={classes.checkeredCard}>
          <img src={content.integrationIntroIconUrl} alt='' width={65} height={65} />
          <Text type={TextName.H3} className={classes.integrationIntroTitle}>
            {content.integrationIntroTitle}
          </Text>
        </CheckeredBgCard>

        {content.integrationOptions.map((option, index) => (
          <GradientOutlineGridBackgroundCard key={index} className={classes.integrationCard}>
            <img src={option.iconUrl} alt='' width={50} height={50} />
            <Text type={TextName.Text} className={classes.integrationText}>{option.text}</Text>
          </GradientOutlineGridBackgroundCard>
        ))}
      </div>

      <DottedBackgroundCard className={classes.dottedCard}>
        <Text type={TextName.Text} className={classes.resourcesNote}>
          {content.resourcesNote}
        </Text>
        <Button as='button' onClick={handleResourcesClick}>
          {content.resourcesButtonText}
        </Button>
      </DottedBackgroundCard>
    </section>
  )
}
